import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const stepsEn = [
  { num: '01', icon: 'ri-phone-line', title: 'Free Consultation', desc: 'Call us or fill out our form. We\'ll discuss your project, answer questions, and schedule a free in-home measurement.' },
  { num: '02', icon: 'ri-ruler-2-line', title: 'Precise Measurement', desc: 'Our expert visits your home, takes precise measurements, and provides a detailed quote — usually within 24 hours.' },
  { num: '03', icon: 'ri-scissors-cut-line', title: 'Custom Fabrication', desc: 'Your glass is cut, tempered, and finished to your exact specifications in our Las Vegas facility. Typical turnaround: 3–5 days.' },
  { num: '04', icon: 'ri-tools-line', title: 'Professional Installation', desc: 'Our certified installers arrive on time, complete the installation cleanly, and leave your space spotless.' },
  { num: '05', icon: 'ri-shield-check-line', title: 'Quality Guarantee', desc: 'We inspect every detail before we leave. All work is backed by our lifetime hardware warranty and 5-year glass warranty.' },
];

const stepsEs = [
  { num: '01', icon: 'ri-phone-line', title: 'Consulta Gratuita', desc: 'Llámenos o complete nuestro formulario. Discutiremos su proyecto y programaremos una medición gratuita en su hogar.' },
  { num: '02', icon: 'ri-ruler-2-line', title: 'Medición Precisa', desc: 'Nuestro experto visita su hogar, toma medidas precisas y proporciona una cotización detallada en 24 horas.' },
  { num: '03', icon: 'ri-scissors-cut-line', title: 'Fabricación Personalizada', desc: 'Su vidrio se corta, templa y termina según sus especificaciones exactas. Tiempo de entrega típico: 3–5 días.' },
  { num: '04', icon: 'ri-tools-line', title: 'Instalación Profesional', desc: 'Nuestros instaladores certificados llegan a tiempo, completan la instalación limpiamente y dejan su espacio impecable.' },
  { num: '05', icon: 'ri-shield-check-line', title: 'Garantía de Calidad', desc: 'Inspeccionamos cada detalle antes de irnos. Todo el trabajo está respaldado por nuestra garantía de por vida en hardware.' },
];

const ProcessSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visibleSteps, setVisibleSteps] = useState<boolean[]>([false, false, false, false, false]);
  const { i18n } = useTranslation();
  const isEs = i18n.resolvedLanguage === 'es';
  const steps = isEs ? stepsEs : stepsEn;

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            steps.forEach((_, i) => {
              setTimeout(() => {
                setVisibleSteps(prev => {
                  const next = [...prev];
                  next[i] = true;
                  return next;
                });
              }, i * 150);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [steps]);

  return (
    <section className="py-20 md:py-28 bg-[#0A0A0A]" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="text-center mb-16">
          <span className="text-xs font-semibold uppercase tracking-widest text-gray-500 mb-3 block">
            {isEs ? 'Cómo Trabajamos' : 'How It Works'}
          </span>
          <h2 className="text-4xl md:text-5xl font-light text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {isEs ? 'Nuestro Proceso Simple' : 'Our Simple Process'}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-0 relative">
          {/* Connecting line */}
          <div className="hidden md:block absolute top-10 left-[10%] right-[10%] h-px bg-gray-800 z-0" />

          {steps.map((step, i) => (
            <div
              key={step.num}
              className={`relative z-10 flex flex-col items-center text-center px-4 transition-all duration-700 ease-out ${
                visibleSteps[i] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              {/* Icon circle */}
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gray-900 border border-gray-700 mb-5 relative group-hover:border-white transition-colors duration-300">
                <i className={`${step.icon} text-white text-2xl`}></i>
                <span className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center rounded-full bg-white text-black text-xs font-bold">
                  {step.num}
                </span>
              </div>
              <h3 className="text-white font-semibold text-sm mb-2">{step.title}</h3>
              <p className="text-gray-500 text-xs leading-relaxed">{step.desc}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-14">
          <Link
            to="/services"
            className="inline-flex items-center gap-3 border border-gray-700 text-white text-sm font-medium px-8 py-4 rounded-full hover:border-white hover:bg-white hover:text-black transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            {isEs ? 'Obtener Cotización Gratis' : 'Get Your Free Quote'}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
